import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Link from 'flarum/common/components/Link';
import Button from 'flarum/common/components/Button';
import DiscussionList from 'flarum/forum/components/DiscussionList';
import DiscussionListState from 'flarum/forum/states/DiscussionListState';
import DiscussionComposer from 'flarum/forum/components/DiscussionComposer';
import LogInModal from 'flarum/forum/components/LogInModal';
import Lightbox from './Lightbox';
import TranslationNotice from './TranslationNotice';
import { formatDate } from '../utils/format';
import { t, tt, route, find, syncLocale, contentLang } from '../i18n';

const PREVIEW = { flyers: 12, photos: 12, tracks: 12, anecdotes: 6 };
const LONG_HISTORY = 4000;
// Nieuwe discussies over een artiest komen onder DJ's & Music › Belgian DJ's.
const COMPOSE_TAGS = ['djs-music', 'belgian-djs'];

/**
 * /artiesten/:slug — één DJ of producer: intro, verhaal, clubs, anekdotes van het forum, flyers, platen en discussies.
 */
export default class ArtistPage extends Page {
  oninit(vnode) {
    super.oninit(vnode);
    this.expanded = {};
    this.lightbox = null;
    syncLocale();
    this.load(m.route.param('slug'));
  }

  onbeforeupdate(vnode) {
    super.onbeforeupdate(vnode);
    const slug = m.route.param('slug');
    if (this.artist && slug !== this.artist.slug()) {
      this.expanded = {};
      this.lightbox = null;
      this.load(slug);
    }
  }

  load(slug) {
    this.artist = app.store.all('lc-artists').find((a) => a.slug() === slug && a.bioHtml() !== undefined) || null;
    this.discussions = null;
    if (this.artist) this.ready();

    find('lc-artists', slug).then((artist) => {
      this.artist = artist;
      this.ready();
      m.redraw();
    });

    if (app.store.all('lc-clubs').length < 20) find('lc-clubs').then(() => m.redraw());
  }

  ready() {
    const artist = this.artist;
    app.history.push('lc.artist', artist.name());
    app.setTitle(artist.name());
    app.setTitleCount(0);

    // Discussies waarin de naam voorkomt (Flarum-zoekopdracht, als exacte zin).
    const q = `"${(artist.searchNames() || [artist.name()])[0]}"`;
    if (!this.discussions || this.discussions.params.q !== q) {
      this.discussions = new DiscussionListState({ q, sort: 'relevance' });
      this.discussions.refresh();
    }
  }

  media() {
    return this.artist.media() || {};
  }

  view() {
    const artist = this.artist;
    if (!artist) return <div className="LcClubPage"><LoadingIndicator /></div>;

    const media = this.media();

    return (
      <div className="LcClubPage LcArtistPage">
        {this.hero()}

        <div className="container LcClubBody">
          <main className="LcClubMain">
            {this.story()}
            {this.members()}
            {/* Voor producers en projecten komen de platen vóór de clubs; voor dj's omgekeerd. */}
            {this.producerFirst() ? this.music() : null}
            {this.clubList()}
            {this.timeline()}
            {this.anecdotes()}
            {this.gallery('flyers', media.flyers, 'LcGallery--flyers')}
            {this.gallery('fotos', media.photos, 'LcGallery--photos')}
            {this.producerFirst() ? null : this.music()}
            {this.forum()}
          </main>

          <aside className="LcClubSide">{this.sidebar()}</aside>
        </div>

        {this.lightbox ? (
          <Lightbox
            items={this.lightbox.items}
            index={this.lightbox.index}
            base=""
            onclose={() => {
              this.lightbox = null;
              m.redraw();
            }}
          />
        ) : null}
      </div>
    );
  }

  hero() {
    const artist = this.artist;
    const media = this.media();
    const portrait = (media.portrait || [])[0] || (media.photos || []).find((p) => p.cover);
    const clubs = this.clubSlugs();

    const facts = [
      { icon: artist.isProject() ? 'fas fa-users' : 'fas fa-headphones', text: this.roleLabel() },
      artist.activeYears() && { icon: 'far fa-calendar', text: artist.activeYears() },
      clubs.length && { icon: 'fas fa-compact-disc', text: t('artist.club_count', { count: clubs.length }) },
    ].filter(Boolean);

    return (
      <section className="LcClubHero LcArtistHero">
        <div className="container LcClubHero-inner">
          <div className="LcClubHero-text">
            <Link className="LcBack" href={route('lc.artists')}>
              <i className="fas fa-arrow-left" aria-hidden="true" /> {t('artist.back')}
            </Link>
            <h1 className="LcClubHero-title">{artist.name()}</h1>
            {artist.shownAliases().length ? <p className="LcAliases">{t('common.aliases', { names: artist.shownAliases().join(' · ') })}</p> : null}
            <ul className="LcFacts">
              {facts.map((f) => (
                <li>
                  <i className={f.icon} aria-hidden="true" /> {f.text}
                </li>
              ))}
            </ul>
            {artist.description() ? <p className="LcClubHero-lead" lang={contentLang(artist)}>{artist.description()}</p> : null}
            <TranslationNotice model={artist} />
            {artist.genres() && artist.genres().length ? (
              <p className="LcCard-genres">{artist.genres().map((g) => <span className="LcChip">{g}</span>)}</p>
            ) : null}
          </div>
          {portrait ? (
            <div className="LcArtistHero-portrait">
              <img src={portrait.thumb || portrait.src} alt={artist.name()} />
            </div>
          ) : null}
        </div>
      </section>
    );
  }

  story() {
    const artist = this.artist;
    if (!artist.bioHtml()) return null;

    const long = artist.bioHtml().length > LONG_HISTORY;
    const open = !long || this.expanded.story;

    return (
      <section className="LcSection LcHistory" id="verhaal">
        <h2>{t('club.history')}</h2>
        <div className={'LcHistory-body' + (open ? '' : ' is-collapsed')} lang={contentLang(artist)}>
          {m.trust(artist.bioHtml())}
        </div>
        {long ? (
          <Button className="Button LcMore" icon={open ? 'fas fa-chevron-up' : 'fas fa-book-open'} onclick={() => (this.expanded.story = !open)}>
            {open ? t('club.read_less') : t('club.read_more')}
          </Button>
        ) : null}
      </section>
    );
  }

  /** "Speelde in": de clubvermeldingen, gegroepeerd per club, oudste club eerst. */
  clubList() {
    const groups = {};
    (this.artist.clubs() || []).forEach((c) => (groups[c.club] = groups[c.club] || []).push(c));
    const slugs = Object.keys(groups);
    if (!slugs.length) return null;

    const club = (s) => app.store.all('lc-clubs').find((c) => c.slug() === s);
    slugs.sort((a, b) => ((club(a) && club(a).opened()) || 9999) - ((club(b) && club(b).opened()) || 9999));

    return (
      <section className="LcSection" id="clubs">
        <div className="LcSection-head">
          <h2>{t('artist.played_at')}</h2>
          <span className="LcSection-count">{slugs.length}</span>
        </div>
        <ul className="LcArtistClubs">
          {slugs.map((s) => {
            const c = club(s);
            return (
              <li>
                <Link href={route('lc.club', { slug: s })} className="LcArtistClubs-club">
                  <strong>{c ? c.name() : s}</strong>
                  {c ? <span>{[c.city(), c.years()].filter(Boolean).join(' · ')}</span> : null}
                </Link>
                <ul className="LcArtistClubs-roles">
                  {groups[s].map((r) => (r.role ? <li>{r.role}</li> : null))}
                </ul>
              </li>
            );
          })}
        </ul>
      </section>
    );
  }

  timeline() {
    const items = this.artist.timeline() || [];
    if (!items.length) return null;

    return (
      <section className="LcSection" id="tijdlijn">
        <h2>{t('club.timeline')}</h2>
        <ol className="LcTimeline" lang={contentLang(this.artist)}>
          {items.map((i) => (
            <li>
              <span className="LcTimeline-year">{i.year}</span>
              <p>{i.text}</p>
            </li>
          ))}
        </ol>
      </section>
    );
  }

  /** Korte citaten uit het forum, elk met een link naar het bericht. */
  anecdotes() {
    const items = this.artist.anecdotes() || [];
    if (!items.length) return null;
    const shown = this.expanded.anecdotes ? items : items.slice(0, PREVIEW.anecdotes);

    return (
      <section className="LcSection" id="anekdotes">
        <div className="LcSection-head">
          <h2>{t('artist.anecdotes')}</h2>
          <span className="LcSection-count">{items.length}</span>
        </div>
        <p className="LcNote">{t('artist.anecdotes_note')}</p>
        <div className="LcAnecdotes">
          {shown.map((a) => (
            <figure className="LcAnecdote">
              <blockquote lang="nl">{a.text}</blockquote>
              <figcaption>
                {a.context ? <span>{a.context}</span> : null}
                {a.date ? <span>{formatDate(a.date)}</span> : null}
                {a.discussion_id ? (
                  <a href={app.forum.attribute('baseUrl') + `/d/${a.discussion_id}/${a.number || 1}`}>
                    {t('artist.read_post')} <i className="fas fa-arrow-right" aria-hidden="true" />
                  </a>
                ) : null}
              </figcaption>
            </figure>
          ))}
        </div>
        {this.moreButton('anecdotes', items.length)}
      </section>
    );
  }

  gallery(id, items, className) {
    if (!items || !items.length) return null;
    const key = id === 'fotos' ? 'photos' : id;
    const shown = this.expanded[key] ? items : items.slice(0, PREVIEW[key]);

    return (
      <section className="LcSection" id={id}>
        <div className="LcSection-head">
          <h2>{t(`club.${key}`)}</h2>
          <span className="LcSection-count">{items.length}</span>
        </div>
        {id === 'flyers' ? <p className="LcNote">{t('artist.flyers_note')}</p> : null}
        <ul className={'LcGallery ' + className}>
          {shown.map((item) => (
            <li>
              <button className="LcGallery-item" onclick={() => (this.lightbox = { items, index: items.indexOf(item) })}>
                <img src={item.thumb} alt={item.caption || ''} loading="lazy" width={item.w} height={item.h} />
                {item.date ? <span className="LcGallery-date">{formatDate(item.date)}</span> : null}
              </button>
              {item.caption ? <p className="LcGallery-caption">{item.caption}</p> : null}
            </li>
          ))}
        </ul>
        {this.moreButton(key, items.length)}
      </section>
    );
  }

  music() {
    const artist = this.artist;
    const tracks = artist.tracks() || [];
    const releases = artist.releases() || [];
    if (!tracks.length && !releases.length) return null;
    const shown = this.expanded.tracks ? tracks : tracks.slice(0, PREVIEW.tracks);

    return (
      <section className="LcSection LcTracks" id="muziek">
        <h2>{t('club.music')}</h2>

        {releases.length ? (
          <div className="LcReleases">
            <h3>{t('artist.releases')}</h3>
            <ul>
              {releases.map((r) => (
                <li>
                  <span className="LcReleases-year">{r.year || '—'}</span>
                  <span>
                    {r.url ? (
                      <a href={r.url} target="_blank" rel="noopener nofollow">
                        {r.title}
                      </a>
                    ) : (
                      r.title
                    )}
                    {r.label ? <em> · {r.label}</em> : null}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {tracks.length ? (
          <ul className="LcTrackGrid">
            {shown.map((track) => (
              <li className="LcTrack">
                {track.youtube_id ? (
                  <a className="LcTrack-thumb" href={`https://www.youtube.com/watch?v=${track.youtube_id}`} target="_blank" rel="noopener noreferrer">
                    <img src={`https://img.youtube.com/vi/${track.youtube_id}/hqdefault.jpg`} alt={`${track.artist || ''} – ${track.title}`} loading="lazy" />
                    <span className="LcTrack-play">
                      <i className="fas fa-play" aria-hidden="true" />
                    </span>
                  </a>
                ) : null}
                <div className="LcTrack-caption">
                  <strong>{track.artist || artist.name()}</strong>
                  <span>{track.title}</span>
                  {track.club ? (
                    <Link href={route('lc.club', { slug: track.club })} className="LcTrack-club">
                      {this.clubName(track.club)}
                    </Link>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        ) : null}
        {this.moreButton('tracks', tracks.length)}
      </section>
    );
  }

  forum() {
    return (
      <section className="LcSection LcDiscussions" id="forum">
        <div className="LcDiscussions-head">
          <h2>{t('artist.discussions', { name: this.artist.name() })}</h2>
          <Button className="Button Button--primary" icon="fas fa-pen" onclick={() => this.compose()}>
            {t('club.start_discussion')}
          </Button>
        </div>
        {this.discussions ? <DiscussionList state={this.discussions} /> : null}
      </section>
    );
  }

  sidebar() {
    const artist = this.artist;
    const facts = [
      [t('artist.fact.roles'), this.roleLabel()],
      [t('artist.fact.real_name'), artist.realName()],
      [t('artist.fact.active'), artist.activeYears()],
      [t('artist.fact.genres'), (artist.genres() || []).join(', ')],
    ].filter(([, v]) => v);

    return [
      <section className="LcPanel LcPanel--facts">
        <h3>{t('club.facts')}</h3>
        <dl className="LcFactList">{facts.map(([k, v]) => [<dt>{k}</dt>, <dd>{v}</dd>])}</dl>
      </section>,

      (artist.projects() || []).length ? (
        <section className="LcPanel">
          <h3>{t('artist.projects')}</h3>
          <ul className="LcNearby">
            {artist.projects().map((p) => (
              <li>
                <Link href={route('lc.artist', { slug: p.slug })}>{p.name}</Link>
                {p.years ? <span>{p.years}</span> : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null,

      (artist.links() || []).length ? (
        <section className="LcPanel">
          <h3>{t('artist.links')}</h3>
          <ul className="LcSources">
            {artist.links().map((l) => (
              <li>
                <a href={l.url} target="_blank" rel="noopener nofollow">
                  {l.title || l.url}
                </a>
              </li>
            ))}
          </ul>
        </section>
      ) : null,

      (artist.sources() || []).length ? (
        <section className="LcPanel">
          <h3>{t('club.sources')}</h3>
          <ul className="LcSources">
            {artist.sources().map((s) => (
              <li>
                <a href={s.url} target="_blank" rel="noopener nofollow ugc">
                  {s.title || s.url}
                </a>
              </li>
            ))}
          </ul>
        </section>
      ) : null,

      <p className="LcNote">{artist.isVerified() ? t('artist.correction') : t('artist.unverified')}</p>,
    ];
  }

  // ---------------------------------------------------------------- rollen en leden

  roleLabel() {
    return this.artist
      .roleList()
      .map((r) => tt(`artist.role.${r}`))
      .join(' · ');
  }

  /** Producers (als hoofdrol) en projecten: platen eerst. */
  producerFirst() {
    return ['producer', 'project'].includes(this.artist.roleList()[0]);
  }

  /** Leden van een project, met link als het lid een eigen pagina heeft. */
  members() {
    const members = this.artist.members() || [];
    if (!members.length) return null;

    return (
      <section className="LcSection" id="leden">
        <h2>{t('artist.members')}</h2>
        <ul className="LcMembers">
          {members.map((m) =>
            typeof m === 'object' && m.slug ? (
              <li>
                <Link href={route('lc.artist', { slug: m.slug })}>{m.name}</Link>
              </li>
            ) : (
              <li>{typeof m === 'object' ? m.name : m}</li>
            )
          )}
        </ul>
      </section>
    );
  }

  // ---------------------------------------------------------------- hulpjes

  clubSlugs() {
    return Array.from(new Set((this.artist.clubs() || []).map((c) => c.club)));
  }

  clubName(slug) {
    const club = app.store.all('lc-clubs').find((c) => c.slug() === slug);
    return club ? club.name() : slug;
  }

  moreButton(key, total) {
    if (total <= PREVIEW[key]) return null;
    const open = this.expanded[key];
    return (
      <Button className="Button LcMore" icon={open ? 'fas fa-chevron-up' : 'fas fa-th'} onclick={() => (this.expanded[key] = !open)}>
        {open ? t('club.show_less') : t('club.show_all', { count: total })}
      </Button>
    );
  }

  compose() {
    if (!app.session.user) {
      app.modal.show(LogInModal);
      return;
    }
    const tags = COMPOSE_TAGS.map((slug) => app.store.all('tags').find((tag) => tag.slug() === slug)).filter(Boolean);
    app.composer.load(DiscussionComposer, { user: app.session.user, tags });
    app.composer.show();
  }
}
